import React, { useState } from "react";
import { ChevronDown, ChevronRight, Lock } from "lucide-react";
import { RATING_META, limiterLooks } from "../../lib/inventorySkills";

const INPUT = "w-full bg-[#0f0f0f] border border-white/10 px-2 py-1.5 text-[13px] text-white";

// Feedback card for a rated item: bold first line, what it is, why it matters, how to build (for the selected rating).
export function FeedbackCard({ skill, rating, open, onToggle }) {
  const meta = RATING_META[rating.rating];
  const looks = rating.rating === "limiter" ? limiterLooks(skill, rating.limiterType) : skill[rating.rating].looks;
  const headline = `${meta.label}: ${looks}`;
  return (
    <div className="mt-2 border-l-2 bg-[#101010]" style={{ borderColor: meta.color }} data-testid={`feedback-${skill.id}`}>
      <button onClick={onToggle} className="w-full text-left px-3 py-2 flex items-start gap-2" data-testid={`feedback-${skill.id}-toggle`}>
        {open ? <ChevronDown className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#757575]" /> : <ChevronRight className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#757575]" />}
        <span className="text-[13px] font-bold text-white leading-snug">{headline}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 pl-8 space-y-2 text-[12px] text-[#c0c0c0]" data-testid={`feedback-${skill.id}-body`}>
          <div><span className="text-[9px] uppercase tracking-[0.3em] font-bold block mb-0.5" style={{ color: meta.color }}>What it is</span>{skill.whatItIs}</div>
          <div><span className="text-[9px] uppercase tracking-[0.3em] font-bold block mb-0.5" style={{ color: meta.color }}>Why it matters</span>{skill.whyItMatters}</div>
          <div><span className="text-[9px] uppercase tracking-[0.3em] font-bold block mb-0.5" style={{ color: meta.color }}>How to build it</span>{skill[rating.rating].build}</div>
        </div>
      )}
    </div>
  );
}

// "+ Evidence": context note, film link, coach-only note (hidden from non-admins by the API).
export function EvidenceFields({ skill, rating, onChange, readOnly, showCoachNote }) {
  const [open, setOpen] = useState(false);
  const has = !!(rating.contextNote || rating.filmLink || rating.coachNote);
  if (!open && !(readOnly && has)) {
    return readOnly ? null : (
      <button onClick={() => setOpen(true)} className="mt-1.5 text-[10px] uppercase tracking-[0.2em] text-[var(--org-accent)] hover:underline" data-testid={`evidence-${skill.id}-btn`}>
        {has ? "Edit evidence" : "+ Evidence"}
      </button>
    );
  }
  return (
    <div className="mt-2 space-y-1.5" data-testid={`evidence-${skill.id}`}>
      <textarea value={rating.contextNote || ""} onChange={e => onChange({ contextNote: e.target.value })} readOnly={readOnly} rows={2} placeholder="Context note (optional)" className={INPUT} data-testid={`evidence-${skill.id}-note`} />
      <input value={rating.filmLink || ""} onChange={e => onChange({ filmLink: e.target.value })} readOnly={readOnly} placeholder="Film link / timestamp (optional)" className={INPUT} data-testid={`evidence-${skill.id}-film`} />
      {showCoachNote && (
        <div className="flex items-start gap-2">
          <Lock className="w-3 h-3 mt-2 text-[#757575] shrink-0" />
          <textarea value={rating.coachNote || ""} onChange={e => onChange({ coachNote: e.target.value })} readOnly={readOnly} rows={2} placeholder="Coach-only note — never shown to players or parents" className={`${INPUT} border-[var(--org-accent)]/30`} data-testid={`evidence-${skill.id}-coach`} />
        </div>
      )}
    </div>
  );
}
